"use client";

import React, { useState } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { TDocumentDefinitions, Content } from "pdfmake/interfaces";
import { Download, ChevronDown, FileText, FileSpreadsheet, FileType } from "lucide-react";
import { getAttributeString } from "@/lib/utils/dynamodb";
import { ReportData, MeetingData, StructureDefinition } from "./types";
import { getFieldOrder, isArrayField } from "./utils";
import * as XLSX from 'xlsx';
// @ts-expect-error - html-docx-js has no type definitions
import htmlDocx from 'html-docx-js/dist/html-docx';

// pdfmakeの設定
type PDFMakeWithVFS = typeof pdfMake & { vfs: typeof pdfFonts.vfs };
(pdfMake as PDFMakeWithVFS).vfs = pdfFonts.vfs;

// PDFコンテンツの型定義
type PdfContentItem = {
  text?: string;
  style?: string;
  alignment?: string;
  bold?: boolean;
  margin?: [number, number, number, number];
  fontSize?: number;
  columns?: Array<{
    text: Array<{
      text: string;
      style: string;
    }>;
  }>;
  ul?: Array<{
    text: string;
  }>;
};

interface ExportOptionsProps {
  reportData: ReportData;
  meetingData: MeetingData;
  structureDefinition?: StructureDefinition;
}

// フォーマット関数
const formatDate = (dateString: string): string => {
  if (!dateString) return "不明";
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (e: unknown) {
    console.error("日時フォーマットエラー:", e);
    return dateString; // パースに失敗した場合は元の文字列を返す
  }
};



// マークダウンテキストをPDF用のコンテンツに変換する関数
const markdownToPdfContent = (text: string): Array<{
  text: string;
  style: string;
  margin: [number, number, number, number];
}> => {
  if (!text) return [];
  
  const lines = text.split('\n');
  const content: Array<{
    text: string;
    style: string;
    margin: [number, number, number, number];
  }> = [];
  
  lines.forEach((line) => {
    const trimmedLine = line.trim();
    
    // 不要な行をスキップ
    if (trimmedLine === '営業日報' || trimmedLine === 'Content' || trimmedLine === 'content' || trimmedLine === '') {
      return;
    }
    
    if (trimmedLine.startsWith('### ')) {
      // レベル3見出し
      content.push({
        text: trimmedLine.substring(4),
        style: 'subSectionHeader',
        margin: [0, 15, 0, 8] as [number, number, number, number]
      });
    } else if (trimmedLine.startsWith('## ')) {
      // レベル2見出し
      content.push({
        text: trimmedLine.substring(3),
        style: 'sectionHeader',
        margin: [0, 20, 0, 10] as [number, number, number, number]
      });
    } else if (trimmedLine.startsWith('# ')) {
      // レベル1見出し
      content.push({
        text: trimmedLine.substring(2),
        style: 'mainHeader',
        margin: [0, 25, 0, 15] as [number, number, number, number]
      });
    } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      // リストアイテム
      content.push({
        text: trimmedLine.substring(2),
        style: 'listItem',
        margin: [20, 2, 0, 2] as [number, number, number, number]
      });
    } else if (trimmedLine) {
      // 通常のテキスト（空行でない場合のみ）
      content.push({
        text: trimmedLine,
        style: 'sectionContent',
        margin: [0, 0, 0, 8] as [number, number, number, number]
      });
    }
  });
  
  return content;
};

// 動的にPDFコンテンツを生成する関数
const generateDynamicContent = (
  meetingData: MeetingData, 
  structureDefinition?: StructureDefinition
): Record<string, unknown>[] => {
  const content: Record<string, unknown>[] = [];
  
  // Contentフィールドがある場合は、その内容のみを処理（タイトルは不要）
  const contentField = meetingData['Content'] || meetingData['content'];
  if (contentField && typeof contentField === 'string') {
    const markdownContent = markdownToPdfContent(contentField);
    if (markdownContent.length > 0) {
      content.push(...markdownContent);
      return content;
    }
  }

  // Contentフィールドがない場合のみ、従来の処理を実行
  if (!structureDefinition) {
    // デフォルトの構造を使用
    return generateDefaultContent(meetingData);
  }

  const fieldOrder = getFieldOrder(structureDefinition).filter(field => field !== 'Content' && field !== 'content');

  // タイトル処理
  const titleField = meetingData.meeting_purpose || meetingData.title || "営業日報";
  content.push({
    text: titleField,
    alignment: 'center',
    fontSize: 24,
    bold: true,
    margin: [0, 0, 0, 30] as [number, number, number, number],
    color: '#1a1a1a'
  });

  // 基本情報セクション（client, datetime）
  const basicFields = ['client', 'datetime'].filter(field => 
    structureDefinition[field] && meetingData[field]
  );

  if (basicFields.length > 0) {
    const columns = basicFields.map(fieldName => {
      const fieldDef = structureDefinition[fieldName];
      const value = meetingData[fieldName];
      const displayValue = fieldName === 'datetime' && typeof value === 'string' 
        ? formatDate(value) 
        : String(value || "未設定");

      return {
        width: '50%',
        text: [
          { text: `${fieldDef.description}\n`, style: 'label' },
          { text: displayValue, style: 'value' }
        ]
      };
    });

    content.push({
      columns,
      margin: [0, 20, 0, 30] as [number, number, number, number]
    });
  }

  // 各フィールドをセクションとして追加
  fieldOrder.forEach(fieldName => {
    // 基本情報フィールドはすでに処理済み
    if (['meeting_purpose', 'client', 'datetime'].includes(fieldName)) {
      return;
    }
    
    // 参加者フィールドは空の時はスキップ
    if (fieldName === 'participants') {
      const participantsValue = meetingData.participants;
      if (!participantsValue || 
          (Array.isArray(participantsValue) && participantsValue.length === 0) ||
          (typeof participantsValue === 'string' && participantsValue.trim() === '')) {
        return;
      }
    }

    const fieldDef = structureDefinition[fieldName];
    const value = meetingData[fieldName];

    // 値が存在しない場合はスキップ
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return;
    }

    // マークダウンテキストの場合は特別処理
    if (typeof value === 'string' && (value.includes('#') || value.includes('-') || value.includes('*'))) {
      const markdownContent = markdownToPdfContent(value);
      if (markdownContent.length > 0) {
        // セクションヘッダーを追加
        content.push({
          text: fieldDef.description,
          style: 'sectionHeader'
        });
        // マークダウンコンテンツを追加
        content.push(...markdownContent);
      }
    } else {
      // 通常の処理
      // セクションヘッダーを追加
      content.push({
        text: fieldDef.description,
        style: 'sectionHeader'
      });

      // 配列型フィールドの処理
      if (isArrayField(fieldDef) && Array.isArray(value)) {
        if (fieldName === 'participants') {
          // 参加者は特別な表示形式
          content.push({
            ul: value.map((item: string) => ({ text: item, style: 'listItem' })),
            style: 'sectionContent'
          });
        } else {
          // その他の配列フィールド
          content.push({
            ul: value.map((item: string) => ({ text: item, style: 'listItem' })),
            style: 'sectionContent'
          });
        }
      } else {
        // 通常のテキストフィールド
        content.push({
          text: String(value),
          style: 'sectionContent'
        });
      }
    }
  });

  return content;
};

// デフォルトコンテンツ生成（後方互換性）
const generateDefaultContent = (meetingData: MeetingData): Record<string, unknown>[] => {
  const content: Record<string, unknown>[] = [];

  // Contentフィールドがある場合は、その内容のみを処理
  if (meetingData['Content'] && typeof meetingData['Content'] === 'string') {
    const markdownContent = markdownToPdfContent(meetingData['Content']);
    if (markdownContent.length > 0) {
      content.push(...markdownContent);
      return content;
    }
  }

  // タイトル
  content.push({
    text: (meetingData.meeting_purpose as string) || "営業日報",
    alignment: 'center',
    fontSize: 24,
    bold: true,
    margin: [0, 0, 0, 30] as [number, number, number, number],
    color: '#1a1a1a'
  });

  // 基本情報
  if (meetingData.client || meetingData.datetime) {
    const columns = [];
    if (meetingData.client) {
      columns.push({
        width: '50%',
        text: [
          { text: 'クライアント\n', style: 'label' },
          { text: meetingData.client as string || "未設定", style: 'value' }
        ]
      });
    }
    if (meetingData.datetime) {
      columns.push({
        width: '50%',
        text: [
          { text: '日時\n', style: 'label' },
          { text: formatDate(meetingData.datetime as string || ''), style: 'value' }
        ]
      });
    }

    content.push({
      columns,
      margin: [0, 20, 0, 30] as [number, number, number, number]
    });
  }

  // 参加者
  if (meetingData.participants && Array.isArray(meetingData.participants) && meetingData.participants.length > 0) {
    content.push({
      text: '参加者',
      style: 'sectionHeader'
    });
    content.push({
      ul: meetingData.participants.map((p: string) => ({ text: p, style: 'listItem' })),
      style: 'sectionContent'
    });
  }

  // デフォルトフィールド
  const defaultFields = [
    { key: 'proposal', label: '提案内容' },
    { key: 'hearing_contents', label: 'ヒアリング内容' },
    { key: 'reaction', label: '反応' },
    { key: 'other', label: 'その他情報' },
    { key: 'next_actions', label: '次のアクション' }
  ];

  defaultFields.forEach(({ key, label }) => {
    const value = meetingData[key];
    if (value) {
      content.push({
        text: label,
        style: 'sectionHeader'
      });
      content.push({
        text: String(value),
        style: 'sectionContent'
      });
    }
  });

  return content;
};

// ファイル名用のタイトルを取得する関数
const getFileNameTitle = (meetingData: MeetingData): string => {
  // 優先順位でタイトルを取得
  const titleCandidates = [
    meetingData.meeting_purpose,
    meetingData.title,
    meetingData['会議タイトル'],
    meetingData['商談内容'],
    meetingData['ヒアリング内容'],
    meetingData['Content'], // Contentフィールドを追加
    meetingData['content'] // contentフィールド（小文字）も追加
  ];

  for (const candidate of titleCandidates) {
    if (candidate && typeof candidate === 'string' && candidate.trim()) {
      // Contentフィールドの場合は、マークダウンの1行目からタイトルを抽出
      if (candidate === meetingData['Content'] || candidate === meetingData['content']) {
        const lines = candidate.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('# ')) {
            const title = trimmedLine.substring(2).trim();
            if (title && title.length > 0) {
              return title;
            }
          } else if (trimmedLine && !trimmedLine.startsWith('##') && !trimmedLine.startsWith('###')) {
            // #で始まらない最初の有効な行をタイトルとして使用
            return trimmedLine;
          }
        }
      } else {
        return candidate.trim();
      }
    }
  }

  // マークダウンの内容からタイトルを抽出（Contentフィールド以外）
  for (const [key, value] of Object.entries(meetingData)) {
    if (key !== 'Content' && typeof value === 'string' && value.includes('#')) {
      // マークダウンの見出しからタイトルを抽出
      const lines = value.split('\n');
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('# ')) {
          const title = trimmedLine.substring(2).trim();
          if (title && title.length > 0) {
            return title;
          }
        }
      }
    }
  }

  // デフォルトのタイトル
  return "営業日報";
};

// Word形式での出力
const exportToWord = (meetingData: MeetingData, structureDefinition?: StructureDefinition) => {
  const content = generateDynamicContent(meetingData, structureDefinition);
  
  // HTML形式でWord文書を作成
  let htmlContent = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head>
      <meta charset='utf-8'>
      <title>レポート</title>
      <style>
        body { font-family: 'MS Gothic', 'Yu Gothic', sans-serif; }
        .title { font-size: 24px; font-weight: bold; text-align: center; margin: 20px 0; }
        .main-header { font-size: 20px; font-weight: bold; margin: 25px 0 15px 0; border-bottom: 2px solid #1a1a1a; }
        .section-header { font-size: 16px; font-weight: bold; margin: 20px 0 10px 0; border-bottom: 1px solid #e0e0e0; }
        .sub-section-header { font-size: 14px; font-weight: bold; margin: 15px 0 8px 0; border-bottom: 1px dotted #c0c0c0; }
        .section-content { font-size: 12px; line-height: 1.5; margin: 0 0 15px 0; }
        .basic-info { margin: 20px 0 30px 0; }
        .basic-info .field { margin: 10px 0; }
        .basic-info .label { font-size: 12px; color: #666; }
        .basic-info .value { font-size: 14px; font-weight: bold; }
        .list-item { font-size: 12px; line-height: 1.4; margin: 2px 0; }
      </style>
    </head>
    <body>
  `;

  content.forEach((item: PdfContentItem) => {
    if (item.text) {
      if (item.style === 'mainHeader') {
        htmlContent += `<div class="main-header">${item.text}</div>`;
      } else if (item.style === 'sectionHeader') {
        htmlContent += `<div class="section-header">${item.text}</div>`;
      } else if (item.style === 'subSectionHeader') {
        htmlContent += `<div class="sub-section-header">${item.text}</div>`;
      } else if (item.style === 'sectionContent') {
        htmlContent += `<div class="section-content">${item.text}</div>`;
      } else if (item.style === 'footer') {
        htmlContent += `<div style="text-align: center; color: #999; margin-top: 30px; font-size: 10px;">${item.text}</div>`;
      } else if (item.alignment === 'center' && item.bold) {
        htmlContent += `<div class="title">${item.text}</div>`;
      } else if (item.columns) {
        htmlContent += `<div class="basic-info">`;
        item.columns.forEach((col: { text: Array<{ text: string; style: string }> }) => {
          if (col.text && Array.isArray(col.text)) {
            htmlContent += `<div class="field">`;
            col.text.forEach((textItem: { text: string; style: string }) => {
              if (textItem.style === 'label') {
                htmlContent += `<div class="label">${textItem.text}</div>`;
              } else if (textItem.style === 'value') {
                htmlContent += `<div class="value">${textItem.text}</div>`;
              }
            });
            htmlContent += `</div>`;
          }
        });
        htmlContent += `</div>`;
      }
    } else if (item.ul) {
      htmlContent += `<div class="section-content"><ul>`;
      item.ul.forEach((listItem: PdfContentItem) => {
        if (listItem.text) {
          htmlContent += `<li class="list-item">${listItem.text}</li>`;
        }
      });
      htmlContent += `</ul></div>`;
    }
  });

  htmlContent += `
    </body>
    </html>
  `;

  // html-docx-jsを使用してHTMLを.docx形式に変換
  const docxBlob = htmlDocx.asBlob(htmlContent);
  
  // ダウンロード用のURLを作成
  const url = URL.createObjectURL(docxBlob);
  const link = document.createElement('a');
  link.href = url;
  
  const fileNameTitle = getFileNameTitle(meetingData);
  link.download = `${fileNameTitle}.docx`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Excel形式での出力
const exportToExcel = (meetingData: MeetingData, structureDefinition?: StructureDefinition) => {
  const content = generateDynamicContent(meetingData, structureDefinition);
  
  // Excelファイル用のデータを準備
  const excelData: Array<Array<string>> = [];
  
  content.forEach((item: PdfContentItem) => {
    if (item.text) {
      if (item.style === 'mainHeader') {
        excelData.push([item.text]);
      } else if (item.style === 'sectionHeader') {
        excelData.push([item.text]);
      } else if (item.style === 'subSectionHeader') {
        excelData.push([item.text]);
      } else if (item.style === 'sectionContent') {
        excelData.push([item.text]);
      } else if (item.alignment === 'center' && item.bold) {
        excelData.push([item.text]);
      } else if (item.columns) {
        item.columns.forEach((col: { text: Array<{ text: string; style: string }> }) => {
          if (col.text && Array.isArray(col.text)) {
            const row: string[] = [];
            col.text.forEach((textItem: { text: string; style: string }) => {
              if (textItem.style === 'label') {
                row.push(textItem.text);
              } else if (textItem.style === 'value') {
                row.push(textItem.text);
              }
            });
            if (row.length > 0) {
              excelData.push(row);
            }
          }
        });
      }
    } else if (item.ul) {
      item.ul.forEach((listItem: PdfContentItem) => {
        if (listItem.text) {
          excelData.push([listItem.text]);
        }
      });
    }
    // 空行を追加
    excelData.push([]);
  });

  // ワークブックとワークシートを作成
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  // 列幅を自動調整
  const colWidths = excelData.reduce((widths: number[], row) => {
    row.forEach((cell, colIndex) => {
      const cellLength = cell ? cell.length : 0;
      if (!widths[colIndex] || cellLength > widths[colIndex]) {
        widths[colIndex] = cellLength;
      }
    });
    return widths;
  }, []);

  worksheet['!cols'] = colWidths.map(width => ({ width: Math.min(Math.max(width + 2, 10), 50) }));

  // ワークシートをワークブックに追加
  XLSX.utils.book_append_sheet(workbook, worksheet, 'レポート');

  // Excelファイルを生成してダウンロード
  const fileNameTitle = getFileNameTitle(meetingData);
  XLSX.writeFile(workbook, `${fileNameTitle}.xlsx`);
};

// PDF形式での出力
const exportToPDF = (reportData: ReportData, meetingData: MeetingData, structureDefinition?: StructureDefinition) => {
  if (!reportData.report) return;

  // PDFオブジェクトが混入しないよう、純粋なデータオブジェクトを作成
  const cleanMeetingData = JSON.parse(JSON.stringify(meetingData));

  // 動的コンテンツ生成
  const dynamicContent = generateDynamicContent(cleanMeetingData, structureDefinition);

  // レポートIDを追加
  dynamicContent.push({
    text: `レポートID: ${getAttributeString(reportData.report?.pk) || reportData.employeeID}-${getAttributeString(reportData.report?.sk) || reportData.reportID}`,
    style: 'footer'
  });

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    content: dynamicContent as unknown as Content,
          styles: {
        label: {
          fontSize: 12,
          color: '#666666',
          margin: [0, 0, 0, 4] as [number, number, number, number]
        },
        value: {
          fontSize: 14,
          color: '#1a1a1a',
          bold: true
        },
        mainHeader: {
          fontSize: 20,
          bold: true,
          color: '#1a1a1a',
          margin: [0, 25, 0, 15] as [number, number, number, number],
          decoration: 'underline',
          decorationStyle: 'solid',
          decorationColor: '#e0e0e0'
        },
        sectionHeader: {
          fontSize: 16,
          bold: true,
          color: '#1a1a1a',
          margin: [0, 20, 0, 10] as [number, number, number, number],
          decoration: 'underline',
          decorationStyle: 'solid',
          decorationColor: '#e0e0e0'
        },
        subSectionHeader: {
          fontSize: 14,
          bold: true,
          color: '#1a1a1a',
          margin: [0, 15, 0, 8] as [number, number, number, number],
          decoration: 'underline',
          decorationStyle: 'dotted',
          decorationColor: '#c0c0c0'
        },
        sectionContent: {
          fontSize: 12,
          color: '#333333',
          lineHeight: 1.5,
          margin: [0, 0, 0, 15] as [number, number, number, number]
        },
        listItem: {
          fontSize: 12,
          color: '#333333',
          lineHeight: 1.4
        },
        footer: {
          fontSize: 10,
          color: '#999999',
          margin: [0, 30, 0, 0] as [number, number, number, number],
          alignment: 'center'
        }
      },
    defaultStyle: {
      font: 'IPAexGothic'
    }
  };

  // 日本語フォントの設定
  const fonts = {
    IPAexGothic: {
      normal: 'https://fonts.gstatic.com/ea/notosansjapanese/v6/NotoSansJP-Regular.otf',
      bold: 'https://fonts.gstatic.com/ea/notosansjapanese/v6/NotoSansJP-Bold.otf',
      italics: 'https://fonts.gstatic.com/ea/notosansjapanese/v6/NotoSansJP-Regular.otf',
      bolditalics: 'https://fonts.gstatic.com/ea/notosansjapanese/v6/NotoSansJP-Bold.otf'
    }
  };

  const fileNameTitle = getFileNameTitle(cleanMeetingData);
  pdfMake.createPdf(docDefinition, undefined, fonts).download(
    `${fileNameTitle}_${getAttributeString(reportData.report?.pk) || reportData.employeeID}-${getAttributeString(reportData.report?.sk) || reportData.reportID}.pdf`
  );
};

export const ExportOptions: React.FC<ExportOptionsProps> = ({ 
  reportData, 
  meetingData, 
  structureDefinition 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleExport = (format: 'pdf' | 'word' | 'excel') => {
    setIsOpen(false);
    
    switch (format) {
      case 'pdf':
        exportToPDF(reportData, meetingData, structureDefinition);
        break;
      case 'word':
        exportToWord(meetingData, structureDefinition);
        break;
      case 'excel':
        exportToExcel(meetingData, structureDefinition);
        break;
    }
  };

  // ドロップダウンの位置を動的に決定
  const getDropdownPosition = () => {
    if (typeof window === 'undefined') return 'left-0 top-full mt-2';
    
    const button = document.querySelector('[data-export-button]') as HTMLElement;
    if (!button) return 'left-0 top-full mt-2';
    
    const buttonRect = button.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    let horizontalPosition = 'left-0';
    let verticalPosition = 'top-full mt-2';
    
    // ボタンが画面の右側にある場合は左側に表示
    if (buttonRect.right > windowWidth - 200) {
      horizontalPosition = 'right-0';
    }
    
    // ボタンが画面の下側にある場合は上側に表示
    if (buttonRect.bottom > windowHeight - 200) {
      verticalPosition = 'bottom-full mb-2';
    }
    
    return `${horizontalPosition} ${verticalPosition}`;
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-export-button
        onClick={() => setIsOpen(!isOpen)}
        className="text-primary text-sm hover:text-primary-hover transition-colors flex items-center gap-2 cursor-pointer bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm px-3 py-1.5 rounded-md border border-gray-200/30 dark:border-gray-700/30"
        aria-label="出力オプション"
      >
        <Download className="h-4 w-4" />
        出力
        <ChevronDown className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          {/* オーバーレイ */}
          <div 
            className="fixed inset-0 z-[9999]"
            onClick={() => setIsOpen(false)}
          />
          
          {/* ドロップダウンメニュー */}
          <div className={`absolute ${getDropdownPosition()} w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-[10000]`}>
            <div className="py-1">
              <button
                onClick={() => handleExport('pdf')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <FileType className="h-4 w-4" />
                PDFで出力
              </button>
              <button
                onClick={() => handleExport('word')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Wordで出力
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excelで出力
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 
